/**
 * Iter 201 — Shared PDF actions (Önizle / İndir / Yazdır).
 *
 * Every controlled PDF button in the app should call these helpers so the
 * user gets a consistent preview-first UX across Master Documents and
 * Document & SOP. All three actions hit the SAME backend endpoint (no
 * duplicate generators) — only the client-side destination differs:
 *
 *   previewPdf  → shared PdfPreviewDialog (inside modal user can zoom, print
 *                 and download from the toolbar)
 *   downloadPdf → visible direct-download button (same blob, saved as file)
 *   printPdf    → hidden iframe + contentWindow.print()
 */
import { openPdfPreview } from "./pdfPreview";

const backendUrl = () => process.env.REACT_APP_BACKEND_URL;

// The app has historically stored the JWT under multiple keys. Read all
// legitimate legacy keys so this helper works whichever the current
// module still uses.
function authToken() {
  return (
    localStorage.getItem("erp_token") ||
    localStorage.getItem("token") ||
    ""
  );
}

async function fetchPdfBlob(pathOrUrl) {
  const isAbs = /^https?:/i.test(pathOrUrl);
  const url = isAbs ? pathOrUrl : `${backendUrl()}${pathOrUrl}`;
  const resp = await fetch(url, {
    headers: { Authorization: `Bearer ${authToken()}` },
  });
  if (!resp.ok) {
    let detail = "";
    try { detail = (await resp.json())?.detail || ""; } catch { /* ignore */ }
    const err = new Error(detail || `PDF alınamadı (${resp.status})`);
    err.status = resp.status;
    throw err;
  }
  return await resp.blob();
}

/**
 * Preview a controlled PDF endpoint in the shared PdfPreviewDialog.
 * The dialog exposes İndir / Yazdır / Kapat inside its own toolbar.
 */
export async function previewPdf(pathOrUrl, filename) {
  try {
    const blob = await fetchPdfBlob(pathOrUrl);
    openPdfPreview(blob, filename || "document.pdf");
  } catch (e) {
    console.error("[previewPdf]", pathOrUrl, e);
    alert(e.message || "PDF oluşturulamadı.");
  }
}

/** Download the same controlled PDF as a file. */
export async function downloadPdf(pathOrUrl, filename) {
  try {
    const blob = await fetchPdfBlob(pathOrUrl);
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = objectUrl;
    a.download = filename || "document.pdf";
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(objectUrl), 5_000);
  } catch (e) {
    console.error("[downloadPdf]", pathOrUrl, e);
    alert(e.message || "PDF indirilemedi.");
  }
}

/** Print the same controlled PDF without opening the preview dialog. */
export async function printPdf(pathOrUrl, filename) {
  try {
    const blob = await fetchPdfBlob(pathOrUrl);
    const objectUrl = URL.createObjectURL(blob);
    const iframe = document.createElement("iframe");
    iframe.style.position = "fixed";
    iframe.style.right = "0";
    iframe.style.bottom = "0";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "0";
    iframe.src = objectUrl;
    iframe.title = filename || "print";
    document.body.appendChild(iframe);
    iframe.onload = () => {
      try {
        iframe.contentWindow?.focus();
        iframe.contentWindow?.print();
      } catch {
        // Cross-origin block fallback → open in new tab so the user can print.
        window.open(objectUrl, "_blank", "noopener,noreferrer");
      }
      setTimeout(() => {
        document.body.removeChild(iframe);
        URL.revokeObjectURL(objectUrl);
      }, 60_000);
    };
  } catch (e) {
    console.error("[printPdf]", pathOrUrl, e);
    alert(e.message || "PDF yazdırılamadı.");
  }
}

/**
 * Resolve the correct PDF endpoint for a Master Document row based on its
 * functional_classification. Returns null when the row does NOT expose a
 * directly-renderable PDF (record-based / not applicable / user confirm).
 *
 * iter327 — PRIORITY: if the row's active revision has a real uploaded file
 * (R2 object), ALWAYS serve the ORIGINAL binary via document-revisions/{rid}/file
 * — never let a reportlab blank-template regenerator shadow a controlled PDF
 * the user uploaded themselves.  Source of truth is the live-joined
 * `has_uploaded_file` flag computed from `document_revisions.file_ref`.
 */
export function resolvePdfEndpoint(row) {
  if (!row) return null;
  // iter327 — uploaded original file wins over any system-generated path.
  if (row.has_uploaded_file && row.active_revision_id) {
    return {
      kind: "uploaded",
      path: `/api/gmp/document-revisions/${row.active_revision_id}/file?inline=1`,
      downloadPath: `/api/gmp/document-revisions/${row.active_revision_id}/file?inline=0`,
    };
  }
  const cls = row.functional_classification;
  const backend = row.source_backend_route || "";
  const id = row.id;
  const docType = (row.document_type || "").toUpperCase();

  const blankPath = `/api/gmp/master-documents/${id}/blank-template`;

  // Managed docs — use classification.
  if (cls === "BLANK_TEMPLATE_REQUIRED") {
    return { kind: "blank", path: blankPath };
  }
  if (cls === "LIVE_LIST_OR_REPORT") {
    if (backend && !backend.includes("{")) {
      return { kind: "live", path: backend };
    }
    return null;
  }
  if (cls === "BOTH_TEMPLATE_AND_OPERATIONAL") {
    return { kind: "blank", path: blankPath };
  }
  if (cls === "CONTROLLED_CONTENT_ONLY") {
    return { kind: "content", path: blankPath };
  }
  if (cls === "OPERATIONAL_PDF_ONLY") {
    // Row-level generic PDF unavailable — must go to source module.
    return null;
  }
  if (cls === "NOT_APPLICABLE" || cls === "USER_CONFIRMATION_REQUIRED") {
    return null;
  }

  // Non-managed / MANUAL docs (SOP, Instruction, controlled content added by
  // user). Fall back to the blank-template endpoint — the shared engine
  // renders a controlled SOP/INSTRUCTION skeleton for these types even when
  // no template definition exists in the registry (Iter213 fallback).
  if (docType === "SOP" || docType === "INSTRUCTION") {
    return { kind: "content", path: blankPath };
  }
  if (docType === "FORM" || docType === "LIST" || docType === "REPORT") {
    return { kind: "blank", path: blankPath };
  }
  return null;
}

export function filenameFor(row, suffix = "") {
  const code = (row?.current_full_code || row?.base_code || "DOK").replace(/\//g, "_");
  const title = (row?.title || "Dokuman").replace(/\s+/g, "_");
  return `${code}_${title}${suffix ? "_" + suffix : ""}.pdf`;
}
